import { postoDoVeiculo } from './configuracaoVeiculo'
import type { PerfilVeiculo } from './perfilVeiculo'
import type { ColocacaoEquipamento, PosicaoEstabilizacao } from './estabilizacao'

export type Ponto3Montagem = [number, number, number]
export interface LigacaoEstabilizacao {
  contato?: string
  ancora?: string
  origem?: string
  destino?: string
  retorno?: boolean
  tensionada: boolean
}
export interface PontoMontagem {
  id: string
  nome: string
  entidade: 'veiculo' | 'inferior' | 'poste' | 'arvore'
  uso: 'contato' | 'ancora'
  ponto: Ponto3Montagem
  zona?: string
  ladoExterno?: number
}
export interface PoseMontagem { y: number; z: number; rx: number; rz: number }

/** Pontos didáticos da estrutura, no mesmo referencial dos veículos da cena. */
export function pontosMontagem(posicao: PosicaoEstabilizacao, p: PerfilVeiculo, pose?: PoseMontagem): PontoMontagem[] {
  const posto = postoDoVeiculo(p, posicao === 'tombado' ? 'lateral' : posicao === 'capotado' ? 'invertido' : 'sobreRodas')
  const complexo = posicao === 'sobreOutroVeiculo'
  const t = pose ?? { y: complexo ? p.tetoY * 1.7 : posto.alturaApoio, z: 0, rx: posto.inclinacaoLongitudinalRad, rz: complexo ? Math.PI : posto.rolagemRad }
  const mundo = ([x, y, z]: Ponto3Montagem): Ponto3Montagem => {
    // Euler XYZ, como o group do veículo (Z seguido de X).
    const xx = x * Math.cos(t.rz) - y * Math.sin(t.rz)
    const yy = x * Math.sin(t.rz) + y * Math.cos(t.rz)
    return [xx, yy * Math.cos(t.rx) - z * Math.sin(t.rx) + t.y, yy * Math.sin(t.rx) + z * Math.cos(t.rx) + t.z]
  }
  const pontos: PontoMontagem[] = []
  // Regiões da estrutura entre os pneus, evitando que o gancho pareça preso à roda.
  const zs = [Math.min(p.portaDianteira.zDianteiro, p.eixoDianteiroZ - p.raioRoda - 0.12), Math.max(p.portaTraseira.zTraseiro + 0.06, p.eixoTraseiroZ + p.raioRoda + 0.12)]
  for (const [i, z] of zs.entries()) for (const lado of [-1, 1]) {
    if (posicao === 'tombado' && lado > 0) continue
    const sufixo = `${i === 0 ? 'dianteiro' : 'traseiro'}-${lado < 0 ? 'motorista' : 'passageiro'}`
    const contato = posicao === 'tombado'
      ? mundo([-(p.largura / 2 - 0.25), p.pisoY, z])
      : mundo([lado * p.largura / 2, posicao === 'capotado' ? p.pisoY + 0.08 : posicao === 'quatroRodas' ? p.cinturaY + 0.25 : p.cinturaY + 0.16, z])
    const ancora = posicao === 'tombado'
      ? mundo([p.largura / 2 - 0.12, p.pisoY, z])
      : mundo([lado * p.largura / 2, posicao === 'quatroRodas' ? p.pisoY : posicao === 'capotado' ? p.pisoY + 0.34 : p.tetoY - 0.12, z])
    const zona = posicao === 'tombado' ? (i === 0 ? 'escora-dianteira' : 'escora-traseira')
      : posicao === 'capotado' ? `escora-capotado-${contato[0] < 0 ? 'esquerda' : 'direita'}`
      : complexo ? `escora-complexa-${contato[0] < 0 ? 'esquerda' : 'direita'}` : undefined
    pontos.push({ id: `contato-${sufixo}`, nome: `Contato ${sufixo.replaceAll('-', ' ')}`, entidade: 'veiculo', uso: 'contato', ponto: contato, zona, ladoExterno: posicao === 'tombado' ? -1 : Math.sign(contato[0]) })
    pontos.push({ id: `ancora-${sufixo}`, nome: `Veículo ${complexo ? 'superior ' : ''}— ancoragem ${sufixo.replaceAll('-', ' ')}`, entidade: 'veiculo', uso: 'ancora', ponto: ancora })
    if (complexo) pontos.push({ id: `inferior-${sufixo}`, nome: `Veículo inferior — ${sufixo.replaceAll('-', ' ')}`, entidade: 'inferior', uso: 'ancora', ponto: [lado * p.largura / 2, p.cinturaY * 0.5, z] })
  }
  pontos.push({ id: 'poste', nome: 'Poste — ponto de passagem', entidade: 'poste', uso: 'ancora', ponto: [-3.3, 0.38, -0.8] })
  pontos.push({ id: 'arvore', nome: 'Árvore — ponto de passagem', entidade: 'arvore', uso: 'ancora', ponto: [3.3, 0.38, 0.8] })
  return pontos
}

const dist = (a: Ponto3Montagem, b: Ponto3Montagem) => Math.hypot(...a.map((n, i) => n - b[i]!))
export function geometriaEscora(base: { x: number; z: number }, contato: Ponto3Montagem, ancora?: Ponto3Montagem) {
  const horizontal = Math.hypot(contato[0] - base.x, contato[2] - base.z)
  const angulo = Math.atan2(Math.max(0, contato[1] - 0.045), horizontal) * 180 / Math.PI
  const comprimento = Math.hypot(horizontal, contato[1] - 0.045)
  const a: Ponto3Montagem = [contato[0] - base.x, contato[1], contato[2] - base.z]
  const b = ancora?.map((n, i) => n - [base.x, 0, base.z][i]!) as Ponto3Montagem | undefined
  const area = b ? Math.hypot(a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]) / 2 : 0
  return { angulo, comprimento, area, faixaAngulo: angulo >= 45 - 1e-6 && angulo <= 60 + 1e-6, faixaComprimento: comprimento >= 1.08 - 1e-6 && comprimento <= 1.8 + 1e-6 }
}

/** Integridade da montagem; não é certificação de estabilidade ou capacidade de carga. */
export function validarLigacao(c: ColocacaoEquipamento, pontos: readonly PontoMontagem[]): { completa: boolean; mensagem: string; zona?: string } {
  const l = c.ligacao
  if (!l) return { completa: false, mensagem: 'Escolha os pontos da ligação.' }
  if (c.equipamento === 'vstrut') {
    const contato = pontos.find(p => p.id === l.contato && p.uso === 'contato')
    const ancora = pontos.find(p => p.id === l.ancora && p.uso === 'ancora')
    if (!contato) return { completa: false, mensagem: 'Base posicionada: escolha o contato da cabeça no veículo.' }
    if (!ancora) return { completa: false, mensagem: 'Escolha a ancoragem inferior da fita no mesmo veículo.' }
    const g = geometriaEscora(c.posicao, contato.ponto, ancora.ponto)
    if ((c.posicao.x - contato.ponto[0]) * (contato.ladoExterno ?? 1) < 0.15)
      return { completa: false, mensagem: 'Mova a base para o lado externo da superfície de contato.' }
    if (contato.entidade !== ancora.entidade || ancora.ponto[1] >= contato.ponto[1] - 0.1 || Math.abs(contato.ponto[2] - ancora.ponto[2]) > 0.3 || g.area < 0.04)
      return { completa: false, mensagem: 'Escolha ancoragem baixa, alinhada ao contato, para fechar o triângulo.' }
    if (!g.faixaAngulo) return { completa: false, mensagem: 'Ajuste a base: referência de inclinação do V-Strut, 45° a 60° em relação ao solo.' }
    if (!g.faixaComprimento) return { completa: false, mensagem: 'Ajuste a base/contato: referência de comprimento do V-Strut, 1,08 a 1,80 m.' }
    return { completa: l.tensionada, mensagem: l.tensionada ? 'Montagem tensionada no exercício.' : 'Triângulo montado: falta tensionar a fita.', zona: contato.zona }
  }
  const origem = pontos.find(p => p.id === l.origem && p.uso === 'ancora')
  const destino = pontos.find(p => p.id === l.destino && p.uso === 'ancora')
  if (!origem || !destino) return { completa: false, mensagem: 'Escolha dois pontos para a catraca.' }
  if (origem.id === destino.id || origem.entidade === destino.entidade || dist(origem.ponto, destino.ponto) < 0.1)
    return { completa: false, mensagem: 'Escolha pontos de dois elementos diferentes.' }
  if (origem.entidade !== 'veiculo' && destino.entidade !== 'veiculo') return { completa: false, mensagem: 'Uma das pontas deve estar no veículo do exercício.' }
  const noInferior = origem.entidade === 'inferior' || destino.entidade === 'inferior'
  const pontoVeiculo = origem.entidade === 'veiculo' ? origem : destino
  const zona = noInferior ? `monobloco-${pontoVeiculo.ponto[0] < 0 ? 'esquerdo' : 'direito'}` : 'amarra-tombado'
  return { completa: l.tensionada, mensagem: l.tensionada ? 'Ligação tensionada no exercício.' : 'Pontos escolhidos: falta tensionar a fita.', zona }
}
